/**
 * TikTok 发品商品图校验与压缩。
 * 对齐官方 Upload Product Image / Create Product：
 * - 格式：JPG / JPEG / PNG / WEBP / BMP（HEIC 浏览器支持差，站内不接）
 * - 体积：≤10MB
 * - 通用尺寸：[100×100, 20000×20000]（平台 code=12038014）
 * - MAIN_IMAGE / ATTRIBUTE_IMAGE：边长 [300, 4000]；比例宜在 3:4～4:3（否则平台会自动拉成 1:1）
 * - DESCRIPTION_IMAGE：通用尺寸即可，保持原图比例
 * - SIZE_CHART_IMAGE：短边 ≥1024
 * 不合规时走与 Temu 同一套 FixedAspectCrop 裁剪门禁（主图 3:4 / 素材 1:1）。
 */

import {
  ASPECT_RATIO_TOLERANCE,
  PRODUCT_CAROUSEL_ASPECT,
  isAspectRatioMatch,
} from '@/components/image/fixedAspectCrop';
import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';

/** Upload Product Image：Max size 10MB */
export const TIKTOK_PRODUCT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const TIKTOK_PRODUCT_IMAGE_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/webp,image/bmp,.jpg,.jpeg,.png,.webp,.bmp';
export const TIKTOK_PRODUCT_IMAGE_HINT =
  '边长 300～4000px · 比例建议 3:4～4:3 · JPG/PNG/WEBP · ≤10MB（超出会压缩）';

/**
 * TTS Create Product `main_images`：至少 1 张、最多 9 张。
 * 不再沿用 Temu 服饰 SKC 轮播 ≥5。
 */
export const TIKTOK_MAIN_IMAGES_MIN = 1;
export const TIKTOK_MAIN_IMAGES_MAX = 9;
/** 细节图上限（主图占 1 席，合计不超过 main_images 上限） */
export const TIKTOK_DETAIL_IMAGES_MAX = TIKTOK_MAIN_IMAGES_MAX - 1;

/** TTS Upload Image use_case：SKU 变体图 */
export const TIKTOK_ATTRIBUTE_IMAGE_USE_CASE = 'ATTRIBUTE_IMAGE';
/** TTS Upload Image use_case：描述内嵌图 */
export const TIKTOK_DESCRIPTION_IMAGE_USE_CASE = 'DESCRIPTION_IMAGE';
/** TTS Upload Image use_case：尺码表图 */
export const TIKTOK_SIZE_CHART_IMAGE_USE_CASE = 'SIZE_CHART_IMAGE';

/** Upload Product Image 通用边长下限（任意 use_case） */
export const TIKTOK_UPLOAD_ABS_MIN = 100;
/** Upload Product Image 通用边长上限（任意 use_case） */
export const TIKTOK_UPLOAD_ABS_MAX = 20000;

/**
 * 组装服饰 SKC 轮播图 URL：主图在前，细节图随后（仅 Tiktok 平台 URL）。
 * @param {object} skc
 * @param {(img: object|null|undefined) => string} [resolveUrl]
 * @returns {string[]}
 */
export function collectTiktokSkcPreviewImgUrls(skc, resolveUrl) {
  const resolve = typeof resolveUrl === 'function'
    ? resolveUrl
    : (img) => (img ? String(img.tiktokUrl || '').trim() : '');
  const urls = [];
  const push = (img) => {
    const u = String(resolve(img) || '').trim();
    if (u && !urls.includes(u)) urls.push(u);
  };
  push(skc?.mainImage);
  (Array.isArray(skc?.detailImages) ? skc.detailImages : []).forEach(push);
  return urls;
}

/**
 * 商品级主图 / 细节图：优先 form 顶层；旧草稿只写在 SKC 行时取第一组有图的色块回填。
 * @param {object} [initial]
 * @returns {{ mainImage: object|null, detailImages: object[] }}
 */
export function resolveTiktokProductLevelImages(initial = {}) {
  const topMain = initial?.mainImage || null;
  const topDetails = Array.isArray(initial?.detailImages) ? initial.detailImages : [];
  if (topMain || topDetails.length) {
    return { mainImage: topMain, detailImages: topDetails };
  }
  for (const skc of Array.isArray(initial?.skcList) ? initial.skcList : []) {
    const mainImage = skc?.mainImage || null;
    const detailImages = Array.isArray(skc?.detailImages) ? skc.detailImages : [];
    if (mainImage || detailImages.length) {
      return { mainImage, detailImages };
    }
  }
  return { mainImage: null, detailImages: [] };
}

const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/x-ms-bmp',
]);
const ALLOWED_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'bmp']);
const RATIO_TOLERANCE = ASPECT_RATIO_TOLERANCE;

/** MAIN_IMAGE / Create Product main_images：边长区间 */
export const TIKTOK_MAIN_MIN = 300;
export const TIKTOK_MAIN_MAX = 4000;
/** 站内裁剪推荐输出（3:4，落在官方允许区间内） */
export const TIKTOK_MAIN_WIDTH = 1200;
export const TIKTOK_MAIN_HEIGHT = 1600;
/** @deprecated 兼容旧名：最小边 */
export const TIKTOK_MAIN_DETAIL_MIN_W = TIKTOK_MAIN_MIN;
export const TIKTOK_MAIN_DETAIL_MIN_H = TIKTOK_MAIN_MIN;
export const TIKTOK_MAIN_DETAIL_MIN = TIKTOK_MAIN_MIN;
export const TIKTOK_MAIN_DETAIL_MAX = TIKTOK_MAIN_MAX;
/** 裁剪源区域下限（低于此难保证清晰，仍允许 ≥300 的合规图） */
export const TIKTOK_CROP_SOURCE_MIN = 300;
/** 官方：MAIN/ATTRIBUTE 比例宜在 3:4～4:3 */
export const TIKTOK_ASPECT_MIN = 3 / 4;
export const TIKTOK_ASPECT_MAX = 4 / 3;
/** SIZE_CHART_IMAGE：短边 ≥1024 */
export const TIKTOK_SIZE_CHART_MIN_SHORT = 1024;

export const TIKTOK_SQUARE_MIN = TIKTOK_MAIN_MIN;
export const TIKTOK_SQUARE_MAX = TIKTOK_MAIN_MAX;
/** 变体/素材图：与 ATTRIBUTE_IMAGE 同限；推荐清晰度 ≥800 */
export const TIKTOK_MATERIAL_SIDE = 800;
export const TIKTOK_MATERIAL_IMAGE_HINT =
  '建议 1:1、边长≥800px（平台允许 300～4000）· JPG/PNG/WEBP · ≤10MB';

function fileExt(name) {
  const base = String(name || '');
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(fixedUi('文件读取失败')));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(fixedUi('图片加载失败')));
    img.src = src;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

function isTiktokAspectInRange(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) return false;
  const ratio = w / h;
  return ratio + RATIO_TOLERANCE >= TIKTOK_ASPECT_MIN
    && ratio - RATIO_TOLERANCE <= TIKTOK_ASPECT_MAX;
}

/** @returns {string|null} 错误文案 */
export function validateTiktokProductImageFormat(file) {
  if (!file) return fixedUi('请选择图片文件');
  const mime = String(file.type || '').trim().toLowerCase();
  const ext = fileExt(file.name);
  if (mime && !ALLOWED_MIME.has(mime)) return fixedUi('仅支持 JPG / PNG / WEBP / BMP 格式');
  if (ext && !ALLOWED_EXT.has(ext)) return fixedUi('仅支持 JPG / PNG / WEBP / BMP 格式');
  if (!mime && !ext) return fixedUi('仅支持 JPG / PNG / WEBP / BMP 格式');
  if (file.size > TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
    return fixedUi('单张图片不能超过 10MB');
  }
  return null;
}

/**
 * 素材/变体图尺寸：宜 1:1，边长落在官方 [300, 4000]；推荐 ≥800。
 * @returns {string|null}
 */
export function validateTiktokMaterialImageDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) {
    return fixedUi('素材图尺寸不符合平台要求：须为正方形，边长 300～4000px');
  }
  if (!isAspectRatioMatch(w, h, 1, RATIO_TOLERANCE)) {
    return fixedUi('素材图尺寸不符合平台要求：须为 1:1');
  }
  if (w < TIKTOK_MAIN_MIN || h < TIKTOK_MAIN_MIN) {
    return fixedUiTpl('素材图过小：须边长≥{0}px（当前 {1}×{2}）', TIKTOK_MAIN_MIN, w, h);
  }
  if (w > TIKTOK_MAIN_MAX || h > TIKTOK_MAIN_MAX) {
    return fixedUiTpl('素材图过大：须边长≤{0}px（当前 {1}×{2}）', TIKTOK_MAIN_MAX, w, h);
  }
  return null;
}

/**
 * 素材图绘制：居中取最大正方形，输出边长 clamp 到 [推荐800, 4000]（不低于 300）。
 */
export function resolveTiktokMaterialDraw(width, height) {
  const w = Math.max(1, Number(width) || 0);
  const h = Math.max(1, Number(height) || 0);
  const side = Math.min(w, h);
  const sx = Math.max(0, Math.floor((w - side) / 2));
  const sy = Math.max(0, Math.floor((h - side) / 2));
  let out = side;
  if (out < TIKTOK_MATERIAL_SIDE) out = Math.max(TIKTOK_MAIN_MIN, out);
  if (out < TIKTOK_MATERIAL_SIDE && side >= TIKTOK_MATERIAL_SIDE) out = TIKTOK_MATERIAL_SIDE;
  if (out > TIKTOK_MAIN_MAX) out = TIKTOK_MAIN_MAX;
  if (out < TIKTOK_MAIN_MIN) out = TIKTOK_MAIN_MIN;
  return { sx, sy, sw: side, sh: side, dw: out, dh: out };
}

/**
 * 校验格式；要求已是 1:1≥300（否则抛 NEED_ASPECT_CROP）；缩放到合格正方形并压缩 ≤10MB。
 */
export async function prepareTiktokMaterialImage(file, { requireAspect = true } = {}) {
  const formatError = validateTiktokProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error(fixedUi('无法读取图片尺寸'));

  if (requireAspect) {
    const dimError = validateTiktokMaterialImageDimensions(width, height);
    if (dimError) {
      const err = new Error(dimError);
      err.code = 'NEED_ASPECT_CROP';
      throw err;
    }
  }

  const draw = resolveTiktokMaterialDraw(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = draw.dw;
  canvas.height = draw.dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(fixedUi('无法处理图片'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, draw.dw, draw.dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, draw.sx, draw.sy, draw.sw, draw.sh, 0, 0, draw.dw, draw.dh);

  const baseName = (file.name || 'material').replace(/\.[^.]+$/, '') || 'material';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-material.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error(fixedUi('素材图压缩后仍超过 10MB，请换一张更小的图'));
}

/**
 * MAIN_IMAGE / 主图细节：边长 [300, 4000]，比例宜在 3:4～4:3。
 * @returns {string|null}
 */
export function validateTiktokProductImageDimensions(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) {
    return fixedUi('图片尺寸不符合平台要求：边长须为 300～4000px，比例建议 3:4～4:3');
  }
  if (w < TIKTOK_MAIN_MIN || h < TIKTOK_MAIN_MIN) {
    return fixedUiTpl('图片过小：边长须≥{0}px（当前 {1}×{2}）', TIKTOK_MAIN_MIN, w, h);
  }
  if (w > TIKTOK_MAIN_MAX || h > TIKTOK_MAIN_MAX) {
    return fixedUiTpl('图片过大：边长须≤{0}px（当前 {1}×{2}）', TIKTOK_MAIN_MAX, w, h);
  }
  if (!isTiktokAspectInRange(w, h)) {
    return fixedUi('图片比例不符合建议：须在 3:4～4:3 之间（超出后平台会自动拉成 1:1）');
  }
  return null;
}

/**
 * 主图绘制：比例不合规时居中取 3:4；超边则等比缩到 ≤4000；不强制放大到 Temu 尺寸。
 */
export function resolveTiktokMainDetailDraw(width, height) {
  const w = Math.max(1, Number(width) || 0);
  const h = Math.max(1, Number(height) || 0);
  const targetRatio = PRODUCT_CAROUSEL_ASPECT;
  const current = w / h;
  let sx = 0;
  let sy = 0;
  let sw = w;
  let sh = h;
  if (!isTiktokAspectInRange(w, h)) {
    if (current > targetRatio) {
      sh = h;
      sw = Math.max(1, Math.round(h * targetRatio));
      sx = Math.max(0, Math.floor((w - sw) / 2));
    } else {
      sw = w;
      sh = Math.max(1, Math.round(w / targetRatio));
      sy = Math.max(0, Math.floor((h - sh) / 2));
    }
  }
  let dw = sw;
  let dh = sh;
  const maxSide = Math.max(dw, dh);
  if (maxSide > TIKTOK_MAIN_MAX) {
    const scale = TIKTOK_MAIN_MAX / maxSide;
    dw = Math.max(TIKTOK_MAIN_MIN, Math.round(dw * scale));
    dh = Math.max(TIKTOK_MAIN_MIN, Math.round(dh * scale));
  }
  return { sx, sy, sw, sh, dw, dh };
}

/**
 * 在保持像素不变的前提下压缩到 ≤ maxBytes（默认 10MB）。
 */
export async function compressTiktokProductImageToLimit(file, maxBytes = TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
  if (!file) throw new Error(fixedUi('无效图片'));
  if (file.size <= maxBytes) return file;

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(fixedUi('无法压缩图片'));

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const baseName = (file.name || 'product').replace(/\.[^.]+$/, '') || 'product';
  let quality = 0.92;
  let best = null;

  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob) {
      best = blob;
      if (blob.size <= maxBytes) {
        return new File([blob], `${baseName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }
    }
    quality -= 0.07;
  }

  if (best && best.size < file.size) {
    throw new Error(fixedUi('图片压缩后仍超过 10MB，请换一张更小的图'));
  }
  throw new Error(fixedUi('图片超过 10MB 且无法有效压缩，请换一张更小的图'));
}

/**
 * 由主图 File 居中裁成 1:1 方块图，边长落在 [300, 4000]，体积 ≤10MB。
 */
export async function cropFileToTiktokSquare(file) {
  if (!file) throw new Error(fixedUi('无效主图'));
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error(fixedUi('无法读取主图尺寸'));

  const cropSide = Math.min(width, height);
  const sx = Math.floor((width - cropSide) / 2);
  const sy = Math.floor((height - cropSide) / 2);

  let outSide = cropSide;
  if (outSide < TIKTOK_MAIN_MIN) outSide = TIKTOK_MAIN_MIN;
  if (outSide > TIKTOK_MAIN_MAX) outSide = TIKTOK_MAIN_MAX;

  const canvas = document.createElement('canvas');
  canvas.width = outSide;
  canvas.height = outSide;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(fixedUi('无法生成方块图'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outSide, outSide);
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, outSide, outSide);

  const baseName = (file.name || 'square').replace(/\.[^.]+$/, '') || 'square';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-square.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error(fixedUi('方块图压缩后仍超过 10MB'));
}

/** 色块图输出边长（列表色块展示） */
export const TIKTOK_COLOR_BLOCK_SIDE = 80;
export const TIKTOK_COLOR_BLOCK_CENTER_RATIO = 0.28;

/**
 * 色块图：从主图正中裁出局部正方形色块，缩放到 80×80 JPG（仅站内预览，禁止上传平台）。
 */
export async function cropFileToTiktokColorBlock(file) {
  if (!file) throw new Error(fixedUi('无效主图'));
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error(fixedUi('无法读取主图尺寸'));

  const minSide = Math.min(width, height);
  const cropSide = Math.max(
    TIKTOK_COLOR_BLOCK_SIDE,
    Math.floor(minSide * TIKTOK_COLOR_BLOCK_CENTER_RATIO),
  );
  const sx = Math.floor((width - cropSide) / 2);
  const sy = Math.floor((height - cropSide) / 2);

  const out = TIKTOK_COLOR_BLOCK_SIDE;
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(fixedUi('无法生成色块图'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out, out);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, out, out);

  const baseName = (file.name || 'color-block').replace(/\.[^.]+$/, '') || 'color-block';
  let quality = 0.92;
  while (quality >= 0.5) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}-color-block.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error(fixedUi('色块图生成失败'));
}

/**
 * 描述内嵌图（DESCRIPTION_IMAGE）：保持比例，边长钳到 [100, 20000]，体积 ≤10MB。
 * 不强制 3:4（官方对 DESCRIPTION_IMAGE 保持原图）。
 */
export async function prepareTiktokDescriptionImage(file) {
  const formatError = validateTiktokProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error(fixedUi('无法读取图片尺寸'));

  let dw = width;
  let dh = height;
  const maxSide = Math.max(dw, dh);
  const minSide = Math.min(dw, dh);
  if (maxSide > TIKTOK_UPLOAD_ABS_MAX) {
    const scale = TIKTOK_UPLOAD_ABS_MAX / maxSide;
    dw = Math.max(TIKTOK_UPLOAD_ABS_MIN, Math.round(dw * scale));
    dh = Math.max(TIKTOK_UPLOAD_ABS_MIN, Math.round(dh * scale));
  } else if (minSide < TIKTOK_UPLOAD_ABS_MIN) {
    const scale = TIKTOK_UPLOAD_ABS_MIN / minSide;
    dw = Math.min(TIKTOK_UPLOAD_ABS_MAX, Math.round(dw * scale));
    dh = Math.min(TIKTOK_UPLOAD_ABS_MAX, Math.round(dh * scale));
  }

  const needsRedraw = dw !== width || dh !== height;
  if (!needsRedraw && file.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(fixedUi('无法处理图片'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dw, dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height, 0, 0, dw, dh);

  const baseName = (file.name || 'description').replace(/\.[^.]+$/, '') || 'description';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error(fixedUi('描述图片压缩后仍超过 10MB，请换一张更小的图'));
}

/**
 * 校验格式与官方尺寸；比例超出 3:4～4:3 时抛 NEED_ASPECT_CROP；
 * 超边长则缩小到 ≤4000，体积压到 ≤10MB。不再强制输出 Temu 的 1340×1785。
 */
export async function prepareTiktokProductImage(file, { requireAspect = true } = {}) {
  const formatError = validateTiktokProductImageFormat(file);
  if (formatError) throw new Error(formatError);

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) throw new Error(fixedUi('无法读取图片尺寸'));

  if (requireAspect) {
    const dimError = validateTiktokProductImageDimensions(width, height);
    if (dimError) {
      const err = new Error(dimError);
      err.code = 'NEED_ASPECT_CROP';
      throw err;
    }
  }

  const draw = resolveTiktokMainDetailDraw(width, height);
  const needsRedraw = draw.sx !== 0 || draw.sy !== 0 || draw.sw !== width || draw.sh !== height
    || draw.dw !== width || draw.dh !== height;

  if (!needsRedraw && file.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = draw.dw;
  canvas.height = draw.dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(fixedUi('无法处理图片'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, draw.dw, draw.dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, draw.sx, draw.sy, draw.sw, draw.sh, 0, 0, draw.dw, draw.dh);

  const baseName = (file.name || 'product').replace(/\.[^.]+$/, '') || 'product';
  let quality = 0.92;
  while (quality >= 0.35) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= TIKTOK_PRODUCT_IMAGE_MAX_BYTES) {
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }
    quality -= 0.07;
  }
  throw new Error(fixedUi('图片压缩后仍超过 10MB，请换一张更小的图'));
}
